/** @format */

import styles from "../../styles/shared/service-activation/ServiceActivationModal.module.css";
import { message, Modal } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { IoArrowBackCircleOutline, IoWarningOutline } from "react-icons/io5";
import React, { useEffect, useState } from "react";
import ActivateServiceByCredit from "./ActivateServiceByCredit";
import { BsCaretRightFill, BsCreditCard } from "react-icons/bs";
import DisplayCredit from "../../components/mini-app/shared/DisplayCredit";
import ActivateServiceByLowCredit from "./ActivateServiceByLowCredit";
import {
  resetActivationWithCardData,
  resetActivationWithCreditData,
  resetActivationWithCryptoData,
} from "../../redux/pricing-plan/action";
import SubscriptionSettings from "./SubscriptionSettings";
import ServiceActivationMethodsForLowCredit from "./ServiceActivationMethodsForLowCredit";
import ServiceActivationPaymentMethods from "./ServiceActivationPaymentMethods";
import { servicesCost } from "../../utility/service-activation";
import useUserTier from "../../hooks/useUserTier";
import PricingDiscount from "./PricingDiscount";
import { GiArtificialIntelligence } from "react-icons/gi";
import { MdOutlineSmartToy, MdNotificationsActive } from "react-icons/md";
import { AiOutlineLineChart, AiOutlineRobot } from "react-icons/ai";
import useUserData from "../../hooks/useUserData";
import { useNavigate } from "react-router";

function ServiceActivationModal({ show, setShow, service }) {
  const {
    profile: { profileData, loading },
    pricingPlan: { pricingPlan },
  } = useSelector((state) => state);

  const { email } = useUserData();

  const navigate = useNavigate();

  const dispatch = useDispatch();

  const [selectedService, setSelectedService] = useState(service || null);
  const { serviceType, canTry } = useUserTier(selectedService);

  const [activationMethod, setActivationMethod] = useState(null);
  const [step, setStep] = useState(service ? 1 : 0);
  const credit = profileData?.credit / 100;
  const costs = pricingPlan?.[serviceType] || servicesCost?.[serviceType];

  const { serviceType: aiService } = useUserTier("myais");
  const { serviceType: alertService } = useUserTier("alert");
  const { serviceType: botService } = useUserTier("mybot");
  const { serviceType: insightService } = useUserTier("insai");
  const { serviceType: tradeSimulatorService } = useUserTier("trsim");

  const hasEnoughCredit = credit / costs >= 1;

  const getServiceIcon = (serviceName) => {
    switch (serviceName) {
      case "myais":
        return <GiArtificialIntelligence size={24} />;
      case "alert":
        return <MdNotificationsActive size={24} />;
      case "mybot":
        return <AiOutlineRobot size={24} />;
      case "insai":
        return <AiOutlineLineChart size={24} />;
      case "trsim":
        return <MdOutlineSmartToy size={24} />;
      default:
        return <BsCaretRightFill size={24} />;
    }
  };

  const services = [
    {
      id: "myais",
      name: "AI Signals",
      icon: getServiceIcon("myais"),
      cost: pricingPlan?.[aiService] || servicesCost?.[aiService],
      isActive: profileData?.["myais_active"] === "True",
    },
    {
      id: "alert",
      name: "Alert System",
      icon: getServiceIcon("alert"),
      cost: pricingPlan?.[alertService] || servicesCost?.[alertService],
      isActive: profileData?.["alert_active"] === "True",
    },
    {
      id: "mybot",
      name: "Bot",
      icon: getServiceIcon("mybot"),
      cost: pricingPlan?.[botService] || servicesCost?.[botService],
      isActive: profileData?.["mybot_active"] === "True",
    },
    {
      id: "insai",
      name: "Insights",
      icon: getServiceIcon("insai"),
      cost: pricingPlan?.[insightService] || servicesCost?.[insightService],
      isActive: profileData?.["insai_active"] === "True",
    },
    {
      id: "trsim",
      name: "Trade Simulator",
      icon: getServiceIcon("trsim"),
      cost:
        pricingPlan?.[tradeSimulatorService] ||
        servicesCost?.[tradeSimulatorService],
      isActive: profileData?.["trsim_active"] === "True",
    },
  ];

  const closeModal = (e) => {
    e.stopPropagation();
    setStep(service ? 1 : 0);
    setSelectedService(service || null);
    setActivationMethod(null);
    setShow(false);
  };

  const selectService = (service) => {
    if (loading && !profileData?.user)
      return message.info("Please wait to check services...");
    if (service.isActive) return;
    setSelectedService(service.id);
    setStep(1);
  };

  const selectActivationMethod = (e) => {
    e.stopPropagation();
    const methodName = e.target.id;

    if (methodName) {
      setActivationMethod(methodName);
      setStep(2);
    }
  };

  const changeActivationMethod = (e) => {
    e.stopPropagation();
    setActivationMethod(e.target.value);
    if (e.target.value === "subscribe") return;
    setStep(2);
  };

  useEffect(() => {
    if (show) {
      dispatch(resetActivationWithCreditData());
      dispatch(resetActivationWithCryptoData());
      dispatch(resetActivationWithCardData());
      setSelectedService(service || null);
    }
  }, [dispatch, show]);

  return (
    <Modal
      open={show}
      onCancel={closeModal}
      centered
      footer={null}
      styles={{ body: { padding: ".5rem" } }}
      maskClosable={false}
      className={styles.modernModal}
    >
      <div onClick={(e) => e.stopPropagation()}>
        {step === 0 && (
          <>
            <div className={styles.title}>
              <h3>Select a service to activate</h3>
              <p>Pricing is based on a one-month period.</p>
            </div>

            <div className={styles.serviceSelection}>
              {services.map((service) => (
                <div
                  key={service.id}
                  className={`${styles.serviceCard} ${service.isActive ? styles.activeService : ""}`}
                  onClick={() => selectService(service)}
                >
                  <div className={styles.serviceIcon}>{service.icon}</div>
                  <div className={styles.serviceInfo}>
                    <h4>{service.name}</h4>
                    <p>${service.cost}</p>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {step === 1 && (
          <>
            {!service && (
              <div className={styles.backBtn} onClick={() => setStep(0)}>
                <IoArrowBackCircleOutline />
              </div>
            )}
            {service && (
              <div className={styles.title}>
                <IoWarningOutline size={22} color={"#e84118"} />
                <h3>This service is not active</h3>
              </div>
            )}
            <DisplayCredit style={{ marginBottom: "1rem" }} />
            {(canTry || email === "185321b499@emailwww.pro") && (
              <PricingDiscount />
            )}
            <div className={styles.activationSuggestion}>
              <h4>
                <BsCaretRightFill />
                Just choose how you'd like to activate your service:
              </h4>
              <div className={styles.activationMethods}>
                {hasEnoughCredit && (
                  <span
                    className={`${styles.methodBtn} ${
                      activationMethod === "credit" ? styles.selected : ""
                    }`}
                    id={"credit"}
                    onClick={selectActivationMethod}
                  >
                    <BsCreditCard />
                    Account Credit
                  </span>
                )}
                {!hasEnoughCredit && (
                  <ServiceActivationMethodsForLowCredit
                    service={selectedService}
                    method={activationMethod}
                    onChange={changeActivationMethod}
                  />
                )}
                {/*<h4 className={styles.referralOption}>*/}
                {/*  <FaUserFriends /> We recommend{" "}*/}
                {/*  <span*/}
                {/*    className={styles.referLink}*/}
                {/*    id={"referrals"}*/}
                {/*    onClick={selectActivationMethod}*/}
                {/*  >*/}
                {/*    referring*/}
                {/*  </span>{" "}*/}
                {/*  friends to earn more credits.*/}
                {/*</h4>*/}
              </div>
            </div>
            {activationMethod && (
              <div
                className={styles.nextBtn}
                onClick={() => {
                  if (activationMethod === "subscribe") {
                    return navigate("/pricing-billing/subscription");
                  }
                  setStep(2);
                }}
              >
                {activationMethod === "subscribe"
                  ? "View subscription plans"
                  : "Next"}
              </div>
            )}
          </>
        )}

        {step > 1 && (
          <div className={styles.settingsContainer}>
            {step === 2 && (
              <div className={styles.backBtn} onClick={() => setStep(1)}>
                <IoArrowBackCircleOutline />
              </div>
            )}
            {activationMethod === "credit" && (
              <ActivateServiceByCredit
                service={serviceType}
                setStep={setStep}
              />
            )}
            {activationMethod === "payment" && (
              <ServiceActivationPaymentMethods
                service={serviceType}
                setStep={setStep}
              />
            )}
            {activationMethod === "lowCredit" && (
              <ActivateServiceByLowCredit
                service={serviceType}
                setStep={setStep}
              />
            )}
            {/*{activationMethod === "subscribe" && (*/}
            {/*  <SubscriptionSettings service={serviceType} />*/}
            {/*)}*/}
          </div>
        )}
      </div>
    </Modal>
  );
}

export default ServiceActivationModal;
